/**
 * Админ-страница CRUD шаблонов анализа менеджера (ADR-019, TM-5743cb35).
 * Доступна по `/admin/analysis-templates`, гейтится через
 * `analysis_template_edit` (admin only). Соседствует с другими
 * `/admin/*` пунктами в сайдбаре.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listAnalysisTemplates } from "@/api/lookups";
import type { AnalysisTemplateResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table/DataTable";
import { AnalysisTemplateForm } from "@/components/registries/AnalysisTemplateForm";

export function AnalysisTemplatesAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AnalysisTemplateResponse | null>(null);
  const [creating, setCreating] = useState(false);

  const templates = useQuery({
    queryKey: ["analysis-templates", "admin-list"],
    queryFn: () => listAnalysisTemplates({ includeInactive: true }),
  });

  const columns: ColumnDef<AnalysisTemplateResponse, unknown>[] = useMemo(
    () => [
      {
        id: "title",
        header: t("analysisTemplates.columns.title"),
        cell: (info) => (
          <span className="font-medium">{info.row.original.title}</span>
        ),
      },
      {
        id: "preview",
        header: t("analysisTemplates.columns.preview"),
        cell: (info) => (
          <span className="line-clamp-2 text-xs text-muted-foreground">
            {info.row.original.prompt_text}
          </span>
        ),
      },
      {
        id: "active",
        header: t("analysisTemplates.columns.active"),
        cell: (info) =>
          info.row.original.is_active
            ? t("managerAnalysis.status.done")
            : t("employees.card.inactive"),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setEditing(info.row.original)}
            data-testid={`edit-analysis-template-${info.row.original.id}`}
          >
            {t("common.edit")}
          </Button>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">
            {t("analysisTemplates.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("analysisTemplates.subtitle")}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="open-create-analysis-template"
        >
          {t("analysisTemplates.createButton")}
        </Button>
      </header>
      <DataTable
        data={templates.data ?? []}
        columns={columns}
        testId="analysis-templates-table"
        isLoading={templates.isLoading}
      />
      <AnalysisTemplateForm
        open={creating || editing !== null}
        existing={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        afterSubmit={() =>
          queryClient.invalidateQueries({ queryKey: ["analysis-templates"] })
        }
      />
    </div>
  );
}
